import { Box, Button, List, ListItem, Typography } from '@mui/material'
import { DailyOrderType } from 'constant'
import { OrderContext } from 'contexts/OrderContextProvider'
import useDailyOrder from 'hooks/useDailyOrder'
import { useContext, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const BoxCodeOrderPicking = () => {
  const { company } = useParams()
  const navigate = useNavigate()
  const {
    dailyOrders,
    handleFetchDailyOrders,
    setSelectedDailyOrderClass,
    selectedDailyOrderClass,
    filteredDailyOrdersByClass,
    selectedDailyOrderBatchIndex,
    setSelectedDailyOrderBatchIndex
  } = useContext(OrderContext)
  const { getDailyOrderBackColor, calculateOrdersLeft } = useDailyOrder()

  const getBatchIndexByLabel = (label?: string | null): number => {
    const normalized = `${label || ''}`.trim().toUpperCase()
    if (!/^[A-Z]+$/.test(normalized)) return Number.MAX_SAFE_INTEGER
    let result = 0
    for (let i = 0; i < normalized.length; i++) {
      result = result * 26 + (normalized.charCodeAt(i) - 64)
    }
    return result - 1
  }
  const getBatchesByLabel = (orders: any[]) => {
    const grouped = new Map<string, any[]>()
    for (const order of orders) {
      const label = `${order?.batchLabel || ''}`.trim().toUpperCase()
      if (!label) continue
      if (!grouped.has(label)) grouped.set(label, [])
      grouped.get(label)!.push(order)
    }
    return Array.from(grouped.entries())
      .sort((a, b) => getBatchIndexByLabel(a[0]) - getBatchIndexByLabel(b[0]))
      .map(([label, batchOrders]) => ({ label, orders: batchOrders }))
  }
  const labeledBatches = getBatchesByLabel(filteredDailyOrdersByClass)

  useEffect(() => {
    if (!company || selectedDailyOrderClass) return
    navigate(`/${company}/daily-order/box-code-order`, { replace: true })
  }, [company, navigate, selectedDailyOrderClass])

  useEffect(() => {
    // Reuse cached orders from context and avoid refetch on back navigation.
    if (dailyOrders.length > 0) {
      return
    }
    handleFetchDailyOrders(
      `${company}`,
      DailyOrderType.BoxCodeSalesOrder,
      undefined
    )
  }, [company, dailyOrders.length])
  const getOrderBox = (order: any) => {
    const itemWithBox = order.orderItems.find((item: any) => item.box)
    return itemWithBox?.box || ''
  }

  const currentBatchOrders =
    selectedDailyOrderBatchIndex !== undefined
      ? (labeledBatches[selectedDailyOrderBatchIndex]?.orders ?? [])
      : []
  const ordersLeftHeader =
    selectedDailyOrderBatchIndex !== undefined
      ? calculateOrdersLeft(currentBatchOrders)
      : calculateOrdersLeft(filteredDailyOrdersByClass)

  const shouldShowBatchResult =
    filteredDailyOrdersByClass.length > 0 &&
    selectedDailyOrderBatchIndex !== undefined

  if (!company || !selectedDailyOrderClass) {
    return null
  }

  return shouldShowBatchResult ? (
    <Box>
      <Box sx={{ margin: '20px', position: 'relative' }}>
        <Button
          variant='outlined'
          onClick={() => {
            setSelectedDailyOrderBatchIndex(undefined)
            navigate(`/${company}/daily-order/box-code-order`)
          }}
          sx={{
            position: 'absolute'
          }}
        >
          Back to batch
        </Button>
        <Typography variant='h5' sx={{ fontWeight: 600, textAlign: 'center' }}>
          {ordersLeftHeader} orders left
        </Typography>
      </Box>
      <Box sx={{ padding: '20px' }}>
        {currentBatchOrders.length > 0 ? (
          currentBatchOrders.map((order, idx) => (
            <Button
              key={order._id || idx}
              fullWidth
              variant='outlined'
              onClick={() =>
                navigate(
                  `/${company}/daily-order/box-code-order/fulfill-order/${order.orderNumber}`,
                  {
                    state: {
                      returnPath: `/${company}/daily-order/box-code-order/fulfill-order`
                    }
                  }
                )
              }
              sx={{
                display: 'block',
                marginBottom: '20px',
                padding: '10px',
                border: '1px solid #ccc',
                fontSize: '36px',
                color: '#000000',
                backgroundColor: getDailyOrderBackColor(order)
              }}
            >
              Order: {order.orderNumber}
              {order.orderItems[0].box ? ` Box: ${getOrderBox(order)}` : ''}
            </Button>
          ))
        ) : (
          <Typography variant='h5' textAlign='center' marginTop='30px'>
            No orders found in this batch.
          </Typography>
        )}
      </Box>
    </Box>
  ) : filteredDailyOrdersByClass.length > 0 ? (
    <Box>
      <Box sx={{ margin: '20px', position: 'relative' }}>
        <Button
          variant='outlined'
          onClick={() => {
            setSelectedDailyOrderClass(undefined)
            setSelectedDailyOrderBatchIndex(undefined)
          }}
          sx={{
            position: 'absolute'
          }}
        >
          Back to last step
        </Button>
        <Typography variant='h5' sx={{ fontWeight: 600, textAlign: 'center' }}>
          {ordersLeftHeader} orders left
        </Typography>
      </Box>
      <List>
        {labeledBatches.map((batch, index) => (
          <ListItem key={index}>
            <Button
              variant='contained'
              sx={{
                width: '100%',
                textAlign: 'center',
                textTransform: 'none',
                fontSize: '20px',
                backgroundColor: '#5a7690'
              }}
              onClick={() => {
                if (selectedDailyOrderBatchIndex === index) {
                  setSelectedDailyOrderBatchIndex(undefined)
                } else {
                  setSelectedDailyOrderBatchIndex(index)
                }
              }}
            >
              {`${batch.label} (${batch.orders.length})`}
            </Button>
          </ListItem>
        ))}
      </List>
      {selectedDailyOrderBatchIndex !== undefined && (
        <Box sx={{ padding: '20px' }}>
          {(labeledBatches[selectedDailyOrderBatchIndex]?.orders ?? []).map(
            (order, idx) => (
              <Button
                key={order._id || idx}
                fullWidth
                variant='outlined'
                onClick={() =>
                  navigate(
                    `/${company}/daily-order/box-code-order/fulfill-order/${order.orderNumber}`,
                    {
                      state: {
                        returnPath: `/${company}/daily-order/box-code-order/fulfill-order`
                      }
                    }
                  )
                }
                sx={{
                  display: 'block',
                  marginBottom: '20px',
                  padding: '10px',
                  border: '1px solid #ccc',
                  fontSize: '36px',
                  color: '#000000',
                  backgroundColor: getDailyOrderBackColor(order)
                }}
              >
                Order: {order.orderNumber}
                {order.orderItems[0].box ? ` Box: ${getOrderBox(order)}` : ''}
              </Button>
            )
          )}
        </Box>
      )}
    </Box>
  ) : (
    <Box>
      <Button
        variant='outlined'
        onClick={() => {
          navigate(-1)
        }}
        sx={{
          margin: '20px'
        }}
      >
        Back
      </Button>
      {dailyOrders.length > 0 ? (
        <List
          sx={{
            '& .MuiListItemButton-root': {
              color: '#607998',
              fontFamily: 'sans-serif',
              fontWeight: 600
            },
            '& a': {
              textDecoration: 'none'
            }
          }}
        >
          {dailyOrders.map(order => (
            <Box key={order.class}>
              <ListItem>
                <Button
                  variant='contained'
                  sx={{
                    width: '100%',
                    textAlign: 'center',
                    textTransform: 'none',
                    fontSize: '26px',
                    padding: '50px 20px',
                    backgroundColor: '#364c63',
                    marginBottom: '20px'
                  }}
                  onClick={() => {
                    setSelectedDailyOrderClass(order.class)
                  }}
                >
                  {order.class}
                </Button>
              </ListItem>
            </Box>
          ))}
        </List>
      ) : (
        <Typography variant='h3' textAlign='center' marginTop='30px'>
          No order found in this field!
        </Typography>
      )}
    </Box>
  )
}

export default BoxCodeOrderPicking
